import React from 'react';
import { X, Crown, Check, Star, Zap, Users, Shield } from 'lucide-react';
import { PRO_MONTHLY_PRICE } from '../lib/proPlans';

interface PremiumUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  title?: string;
  description?: string;
}

const PremiumUpgradeModal: React.FC<PremiumUpgradeModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  title = "Unlock Premium Content",
  description = "Get access to exclusive modules, advanced lessons, and personalized guidance."
}) => {
  if (!isOpen) return null;

  const features = [
    {
      icon: <Crown className="w-5 h-5 text-yellow-500" />,
      title: "Premium Modules",
      description: "Access to exclusive, advanced content"
    },
    {
      icon: <Zap className="w-5 h-5 text-blue-500" />,
      title: "Personalized Guidance",
      description: "AI-powered recommendations and insights"
    },
    {
      icon: <Users className="w-5 h-5 text-green-500" />,
      title: "Priority Support",
      description: "Get help when you need it most"
    },
    {
      icon: <Shield className="w-5 h-5 text-purple-500" />,
      title: "Advanced Analytics",
      description: "Track your progress in detail"
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-t-3xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{title}</h2>
              <p className="text-white/90 text-sm mt-1">{description}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Features */}
          <div className="space-y-4 mb-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-0.5">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 mb-6">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="text-sm font-medium text-gray-700">Most Popular</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{PRO_MONTHLY_PRICE}</div>
              <div className="text-sm text-gray-600">per month</div>
              <div className="text-xs text-gray-500 mt-1">Cancel anytime</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={onUpgrade}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-4 px-6 rounded-2xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <div className="flex items-center justify-center space-x-2">
                <Crown className="w-5 h-5" />
                <span>Upgrade to Premium</span>
              </div>
            </button>
            
            <button
              onClick={onClose}
              className="w-full text-gray-600 py-3 px-6 rounded-2xl font-medium hover:bg-gray-50 transition-colors"
            >
              Maybe Later
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <Check className="w-3 h-3" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center space-x-1">
                <Check className="w-3 h-3" />
                <span>30-Day Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeModal;
